import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  CAlert,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CForm,
  CFormLabel,
  CFormInput,
  CRow,
  CCol,
  CSpinner,
} from "@coreui/react";
import { apiClient } from "services/Api";
import LoadingScreen from "components/globals/LoadingScreen";
import { HotTable } from "@handsontable/react";
import "handsontable/dist/handsontable.full.css";

const CodeMatrixModal = ({
  clientId,
  projectId,
  onClose,
  setToast,
}) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [hotData, setHotData] = useState(null);
  const [colHeaders, setColHeaders] = useState(null);

  // novos estados
  const [startMonth, setStartMonth] = useState("");
  const [endMonth, setEndMonth] = useState("");
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [ppNumber, setPpNumber] = useState("");

  const hotRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    apiClient
      .get(`/projects/management/${clientId}/${projectId}/codes/`)
      .then((response) => {
        const data = response.data;

        setData(data);
        const initialData = data.team.map((person) => {
          const row = [person.name];
          const codes = data.activities.map((activity) => {
            const found = data.codes.find(
              (c) => c.person_id === person.id && c.activity_id === activity.id
            );
            return found ? found.code : "";
          });
          return [...row, ...codes];
        });
        setColHeaders(["Funcionário", ...data.activities.map((a) => a.name)]);
        setHotData(initialData);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setToast({
          title: "Erro",
          message: "Erro ao carregar códigos.",
          type: "error",
        });
        onClose();
      });
  }, [clientId, projectId, onClose, setToast]);

  // garantir que o HOT respeita a largura do modal
  useEffect(() => {
    if (loading) return;

    const resize = () => {
      if (!hotRef.current || !containerRef.current) return;
      const hot = hotRef.current.hotInstance;
      const containerWidth = containerRef.current.clientWidth;

      if (containerWidth > 0) {
        hot.updateSettings({
          width: containerWidth,
        });
        hot.render();
      }
    };

    resize(); // 1ª vez quando o modal abre

    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [loading]);

  const handleAutomate = useCallback(() => {
    if (!hotRef.current || !data) return;

    // validações básicas
    if (!startMonth || !endMonth) {
      setToast({
        title: "Dados em falta",
        message: "Por favor selecione o mês de início e fim.",
        type: "error",
      });
      return;
    }

    if (startMonth > endMonth) {
      setToast({
        title: "Intervalo inválido",
        message: "O mês de início não pode ser posterior ao mês de fim.",
        type: "error",
      });
      return;
    }

    if (!credentials.username || !credentials.password) {
      setToast({
        title: "Credenciais em falta",
        message: "Por favor preencha as credenciais da conta.",
        type: "error",
      });
      return;
    }

    if (!ppNumber.trim()) {
      setToast({
        title: "Dados em falta",
        message: "Por favor preencha o Nº PP.",
        type: "error",
      });
      return;
    }

    const hot = hotRef.current.hotInstance;
    const gridData = hot.getData();

    const codesToSave = [];

    data.team.forEach((person, rowIndex) => {
      data.activities.forEach((activity, colIndex) => {
        const code = gridData[rowIndex][colIndex + 1];

        codesToSave.push({
          person_id: person.id,
          activity_id: activity.id,
          code: code || "",
        });
      });
    });

    setSubmitting(true);

    const userError = (message) => {
      const error = new Error(message);
      error.userMessage = message;
      return error;
    };

    const waitForPaymentRequestUpload = async (taskId) => {
      const maxAttempts = 240;
      const delayMs = 5000;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const resp = await apiClient.get(
          `/projects/management/${clientId}/${projectId}/codes/task/${taskId}/`,
          {
            responseType: "json",
            validateStatus: () => true,
          }
        );

        if (resp.status === 202) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          continue;
        }

        if ((resp.status === 200 || resp.status === 502) && resp.data) {
          return resp;
        }

        throw userError(
          "O upload dos movimentos falhou no servidor. Tente novamente; se o problema persistir, contacte o suporte."
        );
      }

      throw userError(
        "O upload dos movimentos está a demorar mais do que o esperado. Confirme no SGO se os movimentos foram gravados antes de tentar novamente."
      );
    };

    apiClient
      .put(`/projects/management/${clientId}/${projectId}/codes/`, {
        codes: codesToSave,
        start_month: startMonth,
        end_month: endMonth,
        credentials: credentials,
        pp_number: ppNumber.trim(),
        upload_mode: "api_session",
      })
      .then(async (response) => {
        let finalResponse = response;

        if (response.status === 202 && response.data?.task_id) {
          finalResponse = await waitForPaymentRequestUpload(response.data.task_id);
        }

        if (finalResponse.status !== 200) {
          throw {
            response: finalResponse,
          };
        }

        setToast({
          title: "Sucesso",
          message:
            finalResponse?.data?.detail ||
            "Upload do Pedido de Pagamento concluído com sucesso.",
          type: "success",
        });
        onClose();
      })
      .catch((err) => {
        console.error(err);
        setToast({
          title: "Erro",
          message:
            err?.response?.data?.detail ||
            err?.userMessage ||
            (err?.response
              ? "Não foi possível guardar os códigos nem submeter o Pedido de Pagamento no SGO. Tente novamente."
              : "Não foi possível contactar o servidor. Verifique a sua ligação e tente novamente."),
          type: "error",
        });
        setSubmitting(false);
      });
  }, [
    data,
    clientId,
    projectId,
    onClose,
    setToast,
    startMonth,
    endMonth,
    credentials,
    ppNumber,
  ]);

  if (loading) return <LoadingScreen message="A carregar..." />;

  return (
    <CModal
      visible
      onClose={submitting ? undefined : onClose}
      size="xl"
      backdrop={submitting ? "static" : true}
      keyboard={!submitting}
    >
      <CModalHeader onClose={submitting ? undefined : onClose}>
        <CModalTitle>
          Configurar validação SGO do pedido de pagamento
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        {submitting && (
          <CAlert color="info" className="d-flex align-items-start gap-3">
            <CSpinner size="sm" className="mt-1 flex-shrink-0" />
            <div>
              <div className="fw-semibold">A enviar dados para o SGO.</div>
              <div>
                Este processo pode demorar alguns minutos. A janela vai
                fechar automaticamente quando terminar.
              </div>
            </div>
          </CAlert>
        )}

        <div
          style={{
            opacity: submitting ? 0.6 : 1,
            pointerEvents: submitting ? "none" : "auto",
          }}
        >
          {/* Secção de configuração: meses + credenciais */}
          <CForm className="mb-4">
            <CRow className="mb-3">
              <CCol md={6}>
                <CFormLabel>Mês de início</CFormLabel>
                <CFormInput
                  type="month"
                  value={startMonth}
                  disabled={submitting}
                  onChange={(e) => setStartMonth(e.target.value)}
                />
              </CCol>
              <CCol md={6}>
                <CFormLabel>Mês de fim</CFormLabel>
                <CFormInput
                  type="month"
                  value={endMonth}
                  disabled={submitting}
                  onChange={(e) => setEndMonth(e.target.value)}
                />
              </CCol>
            </CRow>
            <CRow>
              <CCol md={6}>
                <CFormLabel>Utilizador da conta</CFormLabel>
                <CFormInput
                  type="text"
                  value={credentials.username}
                  disabled={submitting}
                  onChange={(e) =>
                    setCredentials((prev) => ({
                      ...prev,
                      username: e.target.value,
                    }))
                  }
                />
              </CCol>
              <CCol md={6}>
                <CFormLabel>Password da conta</CFormLabel>
                <CFormInput
                  type="password"
                  value={credentials.password}
                  disabled={submitting}
                  onChange={(e) =>
                    setCredentials((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                />
              </CCol>
            </CRow>
            <CRow className="mt-3">
              <CCol md={6}>
                <CFormLabel>Nº PP</CFormLabel>
                <CFormInput
                  type="text"
                  value={ppNumber}
                  disabled={submitting}
                  onChange={(e) => setPpNumber(e.target.value)}
                />
              </CCol>
            </CRow>
          </CForm>

          {/* wrapper para controlar a largura da tabela */}
          <div
            ref={containerRef}
            style={{ width: "100%", overflowX: "auto" }}
          >
            <HotTable
              ref={hotRef}
              data={hotData}
              colHeaders={colHeaders}
              columnHeaderHeight={60}
              fixedColumnsLeft={1}
              rowHeaders={false}
              rowHeaderWidth={50}
              rowHeights={50}
              colWidths={(index) => (index === 0 ? 90 : 130)}
              autoColumnSize={false}
              licenseKey="non-commercial-and-evaluation"
              className="htCenter"
              stretchH="all"
              height="auto"
              cells={(row, col) => {
                const props = { className: "htCenter htMiddle" };
                if (col === 0) {
                  props.readOnly = true;
                  props.className += " htDimmed fw-bold htGrayCell";
                  props.renderer = "text";
                } else if (submitting) {
                  props.readOnly = true;
                }
                return props;
              }}
            />
          </div>
        </div>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={onClose} disabled={submitting}>
          Cancelar
        </CButton>
        <CButton color="primary" onClick={handleAutomate} disabled={submitting}>
          {submitting && <CSpinner size="sm" className="me-2" />}
          {submitting ? "A sincronizar com o SGO..." : "Validar sessão SGO"}
        </CButton>
      </CModalFooter>


       <style jsx="true">{`
            .htColumnHeaders .colHeader {
        white-space: normal !important;       /* permitir múltiplas linhas */
        overflow: hidden !important;          /* esconder o excesso */
        text-overflow: ellipsis !important;   /* "..." */
        word-break: break-word !important;    /* quebra mesmo palavras longas */
        
        display: -webkit-box !important;
        -webkit-box-orient: vertical !important;
        -webkit-line-clamp: 3;                /* número de linhas antes do "..." */
        line-height: 16px;
        padding: 4px;
      }
    `}</style>
    </CModal>
  );
};

export default CodeMatrixModal;
